import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import type { Scope } from "@sentry/react";
import { sentryFeedback } from "@gui/infrastructure/pass-through/sentryFeedback";

/** The Sentry tag a section boundary puts on the crashes it catches. */
export const SECTION_TAG = "ui.section";

export function useSectionErrorBoundaryViewModel(section: string) {
	const tagSection = useCallback((scope: Scope) => scope.setTag(SECTION_TAG, section), [section]);
	// Opens the feedback form linked to the crash, so the user's account of it lands on the
	// crash's issue in Sentry.
	const reportProblem = useCallback((eventId: string) => {
		void sentryFeedback.openCrashForm(eventId);
	}, []);
	return { tagSection, reportProblem };
}

/**
 * Clears a crashed boundary once the user navigates elsewhere. Runs only while the fallback is
 * shown, so the boundary never remounts its children on an ordinary navigation (the Robots page
 * keeps its state across `/robots/:robotId` changes).
 */
export function useResetOnNavigate(resetError: () => void) {
	const { pathname } = useLocation();
	const crashedAt = useRef(pathname);
	useEffect(() => {
		if (pathname !== crashedAt.current) resetError();
	}, [pathname, resetError]);
}
